import React from "react";
import type { UseFormReturn } from "react-hook-form";
import { Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useGetCampaignAudiencePreviewQuery,
  useGetContactImportsQuery,
  useGetContactListsQuery,
  useGetDealStagesQuery,
  useGetListingsSearchesQuery,
  useGetMembersQuery,
  useGetTagsQuery,
} from "@/store/rest_api";
import { CampaignTargetType } from "@/enum/CampaignTargetType";
import { ContactType } from "@/enum/ContactType";
import { MembershipStatus } from "@/enum/MembershipStatus";
import type { CampaignAudienceRequest } from "@/model/interface/Campaign";
import type { CampaignFormValues } from "@/components/pages/campaigns/CampaignForm";
import { AUDIENCE_SOURCE_LABELS, HAND_PICKED_AUDIENCE, serializeAudienceFilter } from "@/util/campaignAudience";
import { memberName } from "@/util/member";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

const ANY_TYPE = "ANY_TYPE";

const PREVIEW_DELAY_MS = 500;

const CONTACT_TYPE_LABELS: Record<ContactType, string> = {
  [ContactType.LEAD]: "Leads",
  [ContactType.CONTACT]: "Contacts",
  [ContactType.ACCOUNT]: "Accounts",
  [ContactType.OPPORTUNITY]: "Opportunities",
};

interface ChecklistOption<T> {
  value: T;
  label: string;
}

interface CampaignAudienceCardProps {
  form: UseFormReturn<CampaignFormValues>;
}

export default function CampaignAudienceCard({ form }: CampaignAudienceCardProps) {
  const targetType = form.watch("targetType");
  const targetRef = form.watch("targetRef");
  const audienceFilter = form.watch("audienceFilter");
  const isFilter = targetType === CampaignTargetType.FILTER;
  const { data: contactListsPage } = useGetContactListsQuery(
    { size: 100 },
    { skip: targetType !== CampaignTargetType.CONTACT_LIST },
  );
  const { data: tags = [] } = useGetTagsQuery({}, { skip: targetType !== CampaignTargetType.TAG });
  const { data: searches = [] } = useGetListingsSearchesQuery(undefined, {
    skip: targetType !== CampaignTargetType.SAVED_SEARCH,
  });
  const { data: dealStages = [] } = useGetDealStagesQuery({}, { skip: !isFilter });
  const { data: members = [] } = useGetMembersQuery({ status: MembershipStatus.ACTIVE }, { skip: !isFilter });
  const { data: contactImports = [] } = useGetContactImportsQuery(undefined, { skip: !isFilter });
  const request: CampaignAudienceRequest = {
    targetType,
    targetRef: isFilter ? serializeAudienceFilter(audienceFilter) : targetRef,
    enforceCallingHours: form.watch("enforceCallingHours"),
    callingHoursStart: Number(form.watch("callingHoursStart")),
    callingHoursEnd: Number(form.watch("callingHoursEnd")),
  };
  const debouncedRequestKey = useDebouncedValue(JSON.stringify(request), PREVIEW_DELAY_MS);
  const debouncedRequest: CampaignAudienceRequest = JSON.parse(debouncedRequestKey);
  const { data: preview, isFetching: isPreviewing } = useGetCampaignAudiencePreviewQuery(debouncedRequest, {
    skip: !debouncedRequest.targetType || !debouncedRequest.targetRef,
  });
  const contactLists = contactListsPage?.items ?? [];
  const previewStats = preview
    ? [
        { label: "Leads", value: preview.leads },
        { label: "Queued", value: preview.queued },
        { label: "Dialable numbers", value: preview.dialableNumbers },
        { label: "Skipped by Do Not Call", value: preview.skippedDoNotCall },
        { label: "Outside calling hours now", value: preview.outsideCallingHoursNow },
        { label: "Without a phone", value: preview.withoutPhone },
      ]
    : [];

  const renderChecklist = <T extends string | number,>(
    idPrefix: string,
    options: ChecklistOption<T>[],
    selected: T[],
    onChange: (next: T[]) => void,
  ) => (
    <div className="max-h-40 space-y-2 overflow-y-auto rounded-md border border-border p-3">
      {options.length === 0 && <p className="text-sm text-muted-foreground">Nothing to choose yet</p>}
      {options.map((option) => {
        const checkboxId = `${idPrefix}-${option.value}`;
        return (
          <div key={checkboxId} className="flex items-center gap-2">
            <Checkbox
              id={checkboxId}
              checked={selected.includes(option.value)}
              onCheckedChange={(checked) =>
                onChange(
                  checked === true
                    ? [...selected, option.value]
                    : selected.filter((selectedValue) => selectedValue !== option.value),
                )
              }
            />
            <Label htmlFor={checkboxId} className="text-sm font-normal">
              {option.label}
            </Label>
          </div>
        );
      })}
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Audience</CardTitle>
        <CardDescription>
          Who this campaign calls. New matches join the queue while the campaign runs, and archived leads leave it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <FormField control={form.control} name="targetType" render={({ field }) => (
          <FormItem>
            <FormLabel>Audience Source</FormLabel>
            <Select
              value={field.value ?? HAND_PICKED_AUDIENCE}
              onValueChange={(value) => {
                field.onChange(value === HAND_PICKED_AUDIENCE ? undefined : value);
                form.setValue("targetRef", "");
              }}
            >
              <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
              <SelectContent>
                <SelectItem value={HAND_PICKED_AUDIENCE}>Hand-picked leads</SelectItem>
                {Object.values(CampaignTargetType).map((source) => (
                  <SelectItem key={source} value={source}>
                    {AUDIENCE_SOURCE_LABELS[source]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!field.value && (
              <p className="text-xs text-muted-foreground">Pick the leads this campaign calls below.</p>
            )}
            <FormMessage />
          </FormItem>
        )} />

        {targetType === CampaignTargetType.CONTACT_LIST && (
          <FormField control={form.control} name="targetRef" render={({ field }) => (
            <FormItem>
              <FormLabel>Contact List</FormLabel>
              <Select value={field.value || undefined} onValueChange={field.onChange}>
                <FormControl><SelectTrigger><SelectValue placeholder="Select a list" /></SelectTrigger></FormControl>
                <SelectContent>
                  {contactLists.map((list) => (
                    <SelectItem key={list.id} value={list.id}>{list.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )} />
        )}

        {targetType === CampaignTargetType.TAG && (
          <FormField control={form.control} name="targetRef" render={({ field }) => (
            <FormItem>
              <FormLabel>Tag</FormLabel>
              <Select value={field.value || undefined} onValueChange={field.onChange}>
                <FormControl><SelectTrigger><SelectValue placeholder="Select a tag" /></SelectTrigger></FormControl>
                <SelectContent>
                  {tags.map((tag) => (
                    <SelectItem key={tag.id} value={tag.id}>{tag.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )} />
        )}

        {targetType === CampaignTargetType.SAVED_SEARCH && (
          <FormField control={form.control} name="targetRef" render={({ field }) => (
            <FormItem>
              <FormLabel>Saved Property Search</FormLabel>
              <Select value={field.value || undefined} onValueChange={field.onChange}>
                <FormControl><SelectTrigger><SelectValue placeholder="Select a search" /></SelectTrigger></FormControl>
                <SelectContent>
                  {searches.map((search) => (
                    <SelectItem key={search.id} value={String(search.id)}>{search.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )} />
        )}

        {isFilter && (
          <div className="space-y-4">
            <FormField control={form.control} name="audienceFilter.type" render={({ field }) => (
              <FormItem>
                <FormLabel>Type</FormLabel>
                <Select
                  value={field.value || ANY_TYPE}
                  onValueChange={(value) => field.onChange(value === ANY_TYPE ? "" : value)}
                >
                  <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                  <SelectContent>
                    <SelectItem value={ANY_TYPE}>Any type</SelectItem>
                    {Object.values(ContactType).map((contactType) => (
                      <SelectItem key={contactType} value={contactType}>
                        {CONTACT_TYPE_LABELS[contactType]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )} />
            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="audienceFilter.dealStageIds" render={({ field }) => (
                <FormItem>
                  <FormLabel>Deal Stage</FormLabel>
                  {renderChecklist(
                    "audience-stage",
                    dealStages.map((stage) => ({ value: stage.id, label: stage.name })),
                    field.value,
                    field.onChange,
                  )}
                </FormItem>
              )} />
              <FormField control={form.control} name="audienceFilter.assignedMembershipIds" render={({ field }) => (
                <FormItem>
                  <FormLabel>Assigned To</FormLabel>
                  {renderChecklist(
                    "audience-assignee",
                    members.map((member) => ({
                      value: Number(member.id),
                      label: memberName(member, member.user?.email || ""),
                    })),
                    field.value,
                    field.onChange,
                  )}
                </FormItem>
              )} />
            </div>
            <FormField control={form.control} name="audienceFilter.dataImportIds" render={({ field }) => (
              <FormItem>
                <FormLabel>Source</FormLabel>
                {renderChecklist(
                  "audience-import",
                  contactImports.map((contactImport) => ({
                    value: contactImport.id,
                    label: `${contactImport.name} (${contactImport.importedCount})`,
                  })),
                  field.value,
                  field.onChange,
                )}
                <p className="text-xs text-muted-foreground">The import that brought the lead in</p>
              </FormItem>
            )} />
            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="audienceFilter.addedFrom" render={({ field }) => (
                <FormItem>
                  <FormLabel>Added From</FormLabel>
                  <FormControl><Input type="date" {...field} /></FormControl>
                </FormItem>
              )} />
              <FormField control={form.control} name="audienceFilter.addedTo" render={({ field }) => (
                <FormItem>
                  <FormLabel>Added To</FormLabel>
                  <FormControl><Input type="date" {...field} /></FormControl>
                </FormItem>
              )} />
            </div>
          </div>
        )}

        {targetType && (
          <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
            {!debouncedRequest.targetRef ? (
              <p className="text-muted-foreground">Choose who to call to see the audience.</p>
            ) : isPreviewing || !preview ? (
              <p className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Counting leads
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
                {previewStats.map((stat) => (
                  <div key={stat.label}>
                    <div className="text-lg font-semibold tabular-nums">{stat.value.toLocaleString()}</div>
                    <div className="text-xs text-muted-foreground">{stat.label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
